import { DATE_COLUMN_TYPES, NUMBER_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { IColumnTypes } from 'components/spreadsheet/types'
import { ICellValue } from 'types'

// Broader than TEXT_COLUMN_TYPES: link and select values also convert as plain text
const TEXT_KINDS: IColumnTypes[] = ['text', 'link', 'select', 'tag', 'variable', 'phone', 'email']
const SCALAR_KINDS: IColumnTypes[] = [...TEXT_KINDS, ...NUMBER_COLUMN_TYPES, ...DATE_COLUMN_TYPES, 'checkbox']

// For each kind, the kinds whose values survive a change to it. A change from any other
// kind clears every cell. Kinds missing here (Text, URL, Tag, ...) keep every value as text.
// Mirrors get_alter_type_function in api/app/dao/table/field.py — keep the two in step.
const KEPT_FROM: Partial<Record<IColumnTypes, IColumnTypes[]>> = {
  select: [...SCALAR_KINDS, 'multiselect', 'multilink'],
  multiselect: [...SCALAR_KINDS, 'multilink'],
  multilink: [...TEXT_KINDS, 'multiselect'],
  integer: [...TEXT_KINDS, ...NUMBER_COLUMN_TYPES, 'checkbox'],
  float: [...TEXT_KINDS, ...NUMBER_COLUMN_TYPES, 'checkbox'],
  percentage: [...TEXT_KINDS, ...NUMBER_COLUMN_TYPES, 'checkbox'],
  duration: [...TEXT_KINDS, ...NUMBER_COLUMN_TYPES, 'checkbox'],
  date: [...TEXT_KINDS, ...DATE_COLUMN_TYPES],
  datetime: [...TEXT_KINDS, ...DATE_COLUMN_TYPES],
  checkbox: [...TEXT_KINDS, ...NUMBER_COLUMN_TYPES],
  attachment: [],
  vote: [],
  json: []
}

export const kindChangeClearsColumn = (fromKind: IColumnTypes, toKind: IColumnTypes): boolean => {
  const keptFrom = KEPT_FROM[toKind]
  return fromKind !== toKind && keptFrom !== undefined && !keptFrom.includes(fromKind)
}

const LIST_KINDS: IColumnTypes[] = ['multiselect', 'multilink']
const WHOLE_NUMBER_KINDS: IColumnTypes[] = ['integer', 'duration']

export interface KindChangeSummary {
  // kept: every value survives; partial: some values change or are cleared; cleared: every cell is emptied
  outcome: 'kept' | 'partial' | 'cleared'
  detail: string
}

const kept = (detail: string): KindChangeSummary => ({ outcome: 'kept', detail })
const partial = (detail: string): KindChangeSummary => ({ outcome: 'partial', detail })

const summariseToText = (fromKind: IColumnTypes): KindChangeSummary => {
  if (LIST_KINDS.includes(fromKind)) {
    return kept('Each list is written out as text, like ["a", "b"], so changing back restores the separate items.')
  }
  if (fromKind === 'checkbox') return kept('Ticked cells become "true" and unticked cells "false".')
  if (!SCALAR_KINDS.includes(fromKind)) return kept('Each value is kept as its JSON text.')
  return kept('Each value is kept as text.')
}

const summariseToSelect = (fromKind: IColumnTypes): KindChangeSummary => {
  if (LIST_KINDS.includes(fromKind)) {
    return partial('Cells holding one item keep it, but cells holding more than one item will be cleared.')
  }
  return summariseToText(fromKind)
}

const summariseToList = (fromKind: IColumnTypes): KindChangeSummary => {
  if (LIST_KINDS.includes(fromKind)) return kept('Each list is kept as it is.')
  if (TEXT_KINDS.includes(fromKind)) {
    return kept(
      'Each value becomes a one-item list, and text written as a list, like ["a", "b"], is split into its items.'
    )
  }
  return kept('Each value becomes a one-item list.')
}

const summariseToNumber = (fromKind: IColumnTypes, toKind: IColumnTypes): KindChangeSummary => {
  const rounds = WHOLE_NUMBER_KINDS.includes(toKind)
  if (fromKind === 'checkbox') return kept('Ticked cells become 1 and unticked cells 0.')
  if (TEXT_KINDS.includes(fromKind)) {
    const rounding = rounds ? ', rounded to a whole number,' : ''
    return partial(`Text that reads as a number is converted${rounding} and any other text will be cleared.`)
  }
  if (rounds && !WHOLE_NUMBER_KINDS.includes(fromKind)) {
    return partial('Decimals will be rounded to the nearest whole number.')
  }
  return kept('Each number is kept.')
}

const summariseToDate = (fromKind: IColumnTypes): KindChangeSummary => {
  if (fromKind === 'datetime') return partial('The time will be removed and only the date kept.')
  if (fromKind === 'date') return kept('Each date is kept, with a time of midnight.')
  return partial('Text that reads as a date is converted and any other text will be cleared.')
}

const summariseToCheckbox = (fromKind: IColumnTypes): KindChangeSummary => {
  if (NUMBER_COLUMN_TYPES.includes(fromKind)) {
    return partial('Non-zero numbers will be ticked and 0 unticked. The numbers themselves are not kept.')
  }
  return partial('Cells reading "true" or "1" will be ticked. Every other cell will be unticked and its text lost.')
}

// What a kind change does to the column's values, for the confirmation modal.
// Follows the same rules as KEPT_FROM and get_alter_type_function in api/app/dao/table/field.py.
export const summariseKindChange = (fromKind: IColumnTypes, toKind: IColumnTypes): KindChangeSummary => {
  if (kindChangeClearsColumn(fromKind, toKind)) return { outcome: 'cleared', detail: '' }
  if (toKind === 'checkbox') return summariseToCheckbox(fromKind)
  if (toKind === 'select') return summariseToSelect(fromKind)
  if (LIST_KINDS.includes(toKind)) return summariseToList(fromKind)
  if (NUMBER_COLUMN_TYPES.includes(toKind)) return summariseToNumber(fromKind, toKind)
  if (DATE_COLUMN_TYPES.includes(toKind)) return summariseToDate(fromKind)
  return summariseToText(fromKind)
}

// true / '1' / non-zero ticks the box; everything else, including an empty cell, leaves it unticked
const toCheckboxValue = (value: ICellValue): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  return value !== null && value !== undefined && ['true', '1'].includes(String(value).trim().toLowerCase())
}

// a checkbox becomes 1 or 0; text that doesn't read as a number is cleared
const toNumberValue = (value: ICellValue, toKind: IColumnTypes): number | null => {
  if (typeof value === 'boolean') return value ? 1 : 0
  const text = String(value).trim()
  const number = Number(text)
  if (text === '' || Number.isNaN(number)) return null
  return WHOLE_NUMBER_KINDS.includes(toKind) ? Math.round(number) : number
}

// text reading as a JSON list of strings (what a list column becomes as text) is read back into its items;
// any other value becomes a one-item list
const toListValue = (value: ICellValue): ICellValue => {
  if (Array.isArray(value)) return value
  const items = typeof value === 'string' ? parseTextList(value) : null
  if (items) return items.length > 0 ? items : null
  return [String(value)]
}

const parseTextList = (text: string): string[] | null => {
  try {
    const parsed: unknown = JSON.parse(text)
    const isStringList = Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')
    return isStringList ? (parsed as string[]) : null
  } catch {
    return null
  }
}

// Reshape a cached cell for the column's new kind so the grid shows the kept value straight away.
// The rows_updated refresh then swaps in the server's converted value; getting the shape right
// (list vs scalar, text vs number) keeps that refresh from matching a stale dictionary entry.
export const convertCachedCellValue = (value: ICellValue, fromKind: IColumnTypes, toKind: IColumnTypes): ICellValue => {
  if (kindChangeClearsColumn(fromKind, toKind)) return toKind === 'checkbox' ? false : null
  if (toKind === 'checkbox') return toCheckboxValue(value)
  if (value === null || value === undefined) return null
  if (LIST_KINDS.includes(toKind)) return toListValue(value)
  if (toKind === 'select' && Array.isArray(value)) return value.length === 1 ? String(value[0]) : null
  // the server keeps lists and JSON objects as their JSON text
  if (TEXT_KINDS.includes(toKind)) return typeof value === 'object' ? JSON.stringify(value) : String(value)
  if (Array.isArray(value)) return null
  if (NUMBER_COLUMN_TYPES.includes(toKind)) return toNumberValue(value, toKind)
  return value
}
